import { useRef, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Reactable } from "../../components/ui/reactable";
import { DAYWISE_ATTENDANCE } from "../../constants/api";
import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import dayjs from "dayjs";
import { Calendar, Edit } from "lucide-react";
import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import AttendanceEdit from "./AttendanceEdit";
import { useOrgAdminBranchColumn } from "../../hooks/useOrgAdminBranchColumn";

const formatAttendanceStatus = (status) => {
    if (!status || status === "-") return "-";

    const labels = {
        present: "Present",
        absent: "Absent",
        TP: "Present",
        TA: "Absent",
        holiday: "Holiday",
        "on-duty": "On Duty",
        "week-off": "Week Off",
        compensation: "Compensation",
        WFH: "WFH",
        leave: "Leave",
        "N/A": "N/A",
    };

    const normalized = String(status).trim();
    return labels[normalized] || labels[normalized.toLowerCase()] || (
        normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase()
    );
};

const DayWiseAttendance = () => {
    const location = useLocation();
    const selectedDate = useMemo(() => {
        const date = location.state?.date;
        if (!date) return undefined;
        return { date: dayjs(date).format("YYYY-MM-DD") };
    }, [location.state]);
    const [dayWiseAttendanceData, setDayWiseAttendanceData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [selectedAttendanceData, setSelectedAttendanceData] = useState(null);
    const [adminEditDialogOpen, setAdminEditDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [serverTrigger, setServerTrigger] = useState(false);

    const isAdmin = JSON.parse(sessionStorage.getItem("is_admin") || "false");
    const isSuperAdmin = JSON.parse(sessionStorage.getItem("is_super_admin") || "false");
    const { showBranchColumn, insertBranchColumn, TempTransferBadge } = useOrgAdminBranchColumn("employee_name");

    const attendanceEditRef = useRef();

    const handleSave = async () => {
        try {
            setLoading(true);
            if (attendanceEditRef.current) {
                await attendanceEditRef.current.handleSubmit();
            }
            setAdminEditDialogOpen(false);
            setServerTrigger((prev) => !prev);
        } catch (error) {
            console.error("Save failed:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleHide = () => {
        setAdminEditDialogOpen(false);
        setSelectedAttendanceData(null);
    };

    const dayWiseAttendanceColumns = useMemo(() => insertBranchColumn([
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            maxWidth: "300px",
            Cell: ({ row }) => {
                const record = row.original || row;
                const empDetails = {
                    ...record?.employee_details,
                    designation_id: record?.employee_details?.designation
                        ? {
                            id: record?.employee_details.designation.id,
                            designation_name: record?.employee_details.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.employee_details?.employee_name || "-",
                    id: record?.employee_details?.employee_id || "-",
                };

                return (
                    <div className="flex items-center gap-2">
                        <EmployeeDetails details={empDetails} size="xs" />
                        {record?.on_temp_transfer && !showBranchColumn && <TempTransferBadge />}
                    </div>
                );
            },
        },
        {
            accessor: "date",
            Header: "Date",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "status",
            Header: "Status",
            filterable: true,
            Cell: ({ row }) => {
                const record = row.original || row;
                const status = record?.display_status ?? record?.status ?? "-";
                return (
                    <span className="text-gray-900 dark:text-gray-100">
                        {formatAttendanceStatus(status)}
                    </span>
                );
            },
        },
        {
            accessor: "shift",
            Header: "Shift",
            filterable: true,
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "check_in",
            Header: "Check In",
            Cell: ({ value }) =>
                value && dayjs(value).isValid()
                    ? dayjs(value).format("hh:mm A")
                    : "-",
        },
        {
            accessor: "check_out",
            Header: "Check Out",
            Cell: ({ value }) =>
                value && dayjs(value).isValid()
                    ? dayjs(value).format("hh:mm A")
                    : "-",
        },
        {
            accessor: "overtime",
            Header: "Overtime",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "break_hrs",
            Header: "Break Hours",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "total_working_hours",
            Header: "Total Hours",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "location",
            Header: "Location",
            Cell: ({ value }) => value ?? "-",
        },
        ...(
            (isAdmin || isSuperAdmin) ? [
                {
                    accessor: "action",
                    Header: "Action",
                    fixed: true,
                    maxWidth: "70px",
                    align: "right",
                    Cell: ({ row }) => {
                        const record = row;

                        const handleOpenDialog = () => {
                            setSelectedAttendanceData(record);
                            setAdminEditDialogOpen(true);
                        };

                        return (
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                    onClick={handleOpenDialog}
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>
                            </div>
                        );
                    },
                },
            ] : []
        ),
    ]), [insertBranchColumn, isAdmin, isSuperAdmin, showBranchColumn, TempTransferBadge]);

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Calendar}
                title="Day Wise Attendance"
                subtitle="Manage daily attendance records"
                totalRecords={totalRecords}
            />
            <Reactable
                columns={dayWiseAttendanceColumns}
                data={dayWiseAttendanceData}
                setData={setDayWiseAttendanceData}
                setTotalCount={setTotalRecords}
                serverTrigger={serverTrigger}
                url={DAYWISE_ATTENDANCE}
                serverSide={true}
                rowSize="sm"
                isCSVRequired={true}
                isPDFRequired={true}
                isDateRequired={true}
                selectedDate={selectedDate}
            />

            <DialogModal
                open={adminEditDialogOpen}
                onClose={handleHide}
                onSave={handleSave}
                saveLabel="Edit"
                loading={loading}
                title="Edit Attendance"
                width="72rem"
            >
                <AttendanceEdit
                    editDetails={selectedAttendanceData}
                    ref={attendanceEditRef}
                    onSuccess={() => setAdminEditDialogOpen(false)}
                />
            </DialogModal>
        </ContainerCard>
    );
};

export default DayWiseAttendance;
